'use client';

import { useEffect, useMemo, useReducer, useRef } from 'react';

// =============================================================================
// Types
// =============================================================================

export type RewardStatus = 'idle' | 'awaiting_auth' | 'claiming' | 'showing_reward';

export type RewardState = { status: RewardStatus };

export type RewardAction =
  | { type: 'TIMER_COMPLETE'; isAuthenticated: boolean }
  | { type: 'AUTH_SUCCESS' }
  | { type: 'CLAIM_START' }
  | { type: 'REWARD_DISMISSED' }
  | { type: 'RESET' };

export interface UseRewardStateMachineOptions {
  isAuthenticated: boolean;
  rewardPoints: number;
  isComplete: boolean;
}

export interface UseRewardStateMachineReturn {
  status: RewardStatus;
  isAwaitingAuth: boolean;
  isClaiming: boolean;
  isShowingReward: boolean;
  dispatch: React.Dispatch<RewardAction>;
}

// =============================================================================
// Reducer
// =============================================================================

function rewardReducer(state: RewardState, action: RewardAction): RewardState {
  switch (action.type) {
    case 'TIMER_COMPLETE': {
      if (state.status !== 'idle') {
        return state;
      }
      return action.isAuthenticated ? { status: 'claiming' } : { status: 'awaiting_auth' };
    }

    case 'AUTH_SUCCESS': {
      if (state.status !== 'awaiting_auth') {
        return state;
      }
      return { status: 'claiming' };
    }

    case 'CLAIM_START': {
      if (state.status !== 'claiming') {
        return state;
      }
      return { status: 'showing_reward' };
    }

    case 'REWARD_DISMISSED':
    case 'RESET':
      return { status: 'idle' };

    default:
      return state;
  }
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Platform-agnostic reward state machine.
 * Manages state transitions for the reward flow without UI concerns.
 * Use this in web and mobile to handle timer completion → auth → claim flow.
 */
export function useRewardStateMachine({
  isAuthenticated,
  rewardPoints,
  isComplete,
}: UseRewardStateMachineOptions): UseRewardStateMachineReturn {
  const [state, dispatch] = useReducer(rewardReducer, { status: 'idle' });

  // Guard against duplicate TIMER_COMPLETE dispatch
  // Even though the reducer defends against it, this explicit guard
  // prevents unnecessary dispatch calls during re-renders
  const hasDispatchedRef = useRef(false);

  // Handle timer completion
  useEffect(() => {
    if (!isComplete) {
      // Reset guard when timer is no longer complete (new cycle)
      hasDispatchedRef.current = false;
      if (state.status !== 'idle') {
        dispatch({ type: 'RESET' });
      }
      return;
    }

    // Only dispatch TIMER_COMPLETE once per timer completion cycle
    if (state.status === 'idle' && rewardPoints > 0 && !hasDispatchedRef.current) {
      hasDispatchedRef.current = true;
      dispatch({ type: 'TIMER_COMPLETE', isAuthenticated });
    }
  }, [isComplete, isAuthenticated, rewardPoints, state.status]);

  return useMemo(
    () => ({
      status: state.status,
      isAwaitingAuth: state.status === 'awaiting_auth',
      isClaiming: state.status === 'claiming',
      isShowingReward: state.status === 'showing_reward',
      dispatch,
    }),
    [state.status],
  );
}
